import React, { memo, useMemo, useCallback, useRef } from "react";
import { Container, useApp } from "@pixi/react";
import { Settings } from "../../settings";
import PotCollectionReel from "./PotCollectionReel";
import { useReelRefs } from "../../Contexts/ReelRefsContext";
import SpineTextureExtractor from "../../utils/SpineTextureExtractor";
import FontGenerator from "../../utils/FontGenerator";
import { useSelector } from "react-redux";
import { selectPotCollectionInitialRespinScreen } from "../../State/selectors";

/**
 * PotCollectionReelSet
 *
 * Top-level reel grid for the Pot Collection feature.
 * Always visible — the background animation handles viewport shifting.
 * Constructs as soon as initialRespinScreen data arrives.
 *
 * Key difference from RoyalCollect: no blurred symbols spritesheet needed.
 */
const PotCollectionReelSet = memo(
  ({ symbolWidth, symbolHeight, reelSpacing }) => {
    const app = useApp();
    const { potCollectionReelRefs } = useReelRefs();
    const initialReels = useSelector(selectPotCollectionInitialRespinScreen);
    const reelRefs = useRef([]);

    const setReelRef = useCallback(
      (index) => (el) => {
        reelRefs.current[index] = el;
        if (potCollectionReelRefs.current) {
          potCollectionReelRefs.current[index] = el;
        }
      },
      [potCollectionReelRefs],
    );

    // Extract spine textures (no blurred needed for PC)
    const symbolsSpriteSheet = useMemo(() => {
      if (app?.renderer) {
        SpineTextureExtractor.initialize(app.renderer);
        FontGenerator.initialize(app.renderer);
      }
      return SpineTextureExtractor.getSymbols();
    }, [app?.renderer]);

    // Container Y position — same formula as RC/H&W
    const containerY = useMemo(
      () => -symbolHeight * (0.5 * Settings.SYMBOLS_VISIBLE - 0.5),
      [symbolHeight],
    );

    // Reel X positions
    const reelPositions = useMemo(() => {
      if (!initialReels || initialReels.length === 0) return [];
      const centerOffset = (Settings.REEL_COUNT - 1) / 2;
      return initialReels.map((_, index) => ({
        x: reelSpacing * (index - centerOffset),
      }));
    }, [initialReels, reelSpacing]);

    // Guard: Don't render until data and textures ready
    if (
      !initialReels ||
      !Array.isArray(initialReels) ||
      initialReels.length === 0 ||
      !symbolsSpriteSheet?.textures
    ) {
      return null;
    }

    return (
      <Container
        name="pot_collection_reelset"
        zIndex={3}
        x={0}
        y={containerY}
      >
        {initialReels.map((reel, index) => {
          const position = reelPositions[index];
          if (!position) return null;

          return (
            <PotCollectionReel
              key={index}
              ref={setReelRef(index)}
              reel={reel}
              index={index}
              x={position.x}
              symbolWidth={symbolWidth}
              symbolHeight={symbolHeight}
              symbolsSpriteSheet={symbolsSpriteSheet}
            />
          );
        })}
      </Container>
    );
  },
);

PotCollectionReelSet.displayName = "PotCollectionReelSet";

export default PotCollectionReelSet;
